import type { ReactNode } from 'react'
import type { Descendant, TElement } from 'platejs'
import { ExternalLinkIcon, Link } from '@buffer-mono/popcorn'

import { fromNoteContent, HEADING_KEY } from './noteRichContent'
import type { NoteContent } from './noteRichContent'
import { noteLinkHref } from './noteLinkHref'

import styles from './NoteContentView.module.css'

function renderRawLinkChildren(
  children: Descendant[],
  linkText: string,
): ReactNode[] {
  let protocolLength = linkText.match(/^https?:\/\//i)?.[0].length ?? 0
  return children.map((child, index) => {
    if (!('text' in child) || typeof child.text !== 'string')
      return renderNode(child, index)
    const text = child.text.slice(protocolLength)
    protocolLength = Math.max(0, protocolLength - child.text.length)
    return renderNode({ ...child, text }, index)
  })
}

const renderNode = (node: Descendant, key: number): ReactNode => {
  if ('text' in node && typeof node.text === 'string') {
    let content: ReactNode = node.text
    if (node.strikethrough === true) content = <s>{content}</s>
    if (node.italic === true) content = <em>{content}</em>
    if (node.bold === true) content = <strong>{content}</strong>
    return <span key={key}>{content}</span>
  }
  if (!('children' in node) || !Array.isArray(node.children)) return null
  if (node.type === 'mention' && typeof node.value === 'string') {
    let content: ReactNode = `${typeof node.trigger === 'string' ? node.trigger : '@'}${node.value}`
    const marks = node.children[0]
    if (marks?.strikethrough === true) content = <s>{content}</s>
    if (marks?.italic === true) content = <em>{content}</em>
    if (marks?.bold === true) content = <strong>{content}</strong>
    return (
      <span key={key} className={styles.mention}>
        {content}
      </span>
    )
  }
  const content = node.children.map(renderNode)
  if (node.type === 'a' && typeof node.url === 'string') {
    const href = noteLinkHref(node.url)
    const linkText = node.children
      .map((child) => ('text' in child ? child.text : ''))
      .join('')
    const isRawLink = href !== undefined && noteLinkHref(linkText) === href
    return href ? (
      <Link
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={isRawLink ? styles.rawLink : undefined}
        aria-label={isRawLink ? linkText : undefined}
        title={isRawLink ? href : undefined}
      >
        {isRawLink ? (
          <>
            <ExternalLinkIcon size="small" />
            <span className={styles.rawLinkText}>
              {renderRawLinkChildren(node.children, linkText)}
            </span>
          </>
        ) : (
          content
        )}
      </Link>
    ) : (
      <span key={key}>{content}</span>
    )
  }
  return <span key={key}>{content}</span>
}

const renderLine = (children: Descendant[]): ReactNode =>
  children.length === 1 && 'text' in children[0] && children[0].text === '' ? (
    <br aria-hidden="true" />
  ) : (
    children.map(renderNode)
  )

const listItemChildren = (item: Descendant): Descendant[] =>
  'children' in item && Array.isArray(item.children)
    ? item.children.flatMap((child: Descendant) =>
        'children' in child &&
        Array.isArray(child.children) &&
        child.type === 'lic'
          ? child.children
          : [child],
      )
    : []

const renderBlock = (block: TElement, key: number): ReactNode => {
  if (block.type === HEADING_KEY)
    return (
      <h3 key={key} className={styles.heading}>
        {block.children.map(renderNode)}
      </h3>
    )
  if (block.type === 'ul' || block.type === 'ol') {
    const List = block.type
    return (
      <List key={key} className={styles.list}>
        {block.children.map((item, index) => (
          // oxlint-disable-next-line react/no-array-index-key -- Read-only items are rebuilt from immutable note text.
          <li key={index}>{listItemChildren(item).map(renderNode)}</li>
        ))}
      </List>
    )
  }
  return <div key={key}>{renderLine(block.children)}</div>
}

export function NoteContentView({
  content,
}: {
  content: NoteContent
}): React.ReactElement {
  return (
    <div className={styles.content}>
      {/* Read-only blocks are rebuilt from immutable note text, so the index is a stable key. */}
      {fromNoteContent(content).map(renderBlock)}
    </div>
  )
}
